// src/components/navigation/NavigationContainer.jsx
// Updated to use 3D overview first, then step-by-step waypoint navigation
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  IconButton,
  Paper,
  Backdrop,
  CircularProgress,
  Alert,
  Snackbar,
  Button,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
  Fab,
} from '@mui/material';
import {
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  ArrowBack as ArrowBackIcon,
  ThreeDRotation as ThreeDIcon,
  Navigation as NavigationIcon,
} from '@mui/icons-material';
import { useNavigation } from '../../contexts/NavigationContext';
import NavigationMap from './NavigationMap';
import StepCard from './StepCard';
import ArrivalScreen from './ArrivalScreen';
import Building3DViewer from '../maps/preview/Building3DViewer';
import indoorMappingService from '../../services/indoorMappingService';

/**
 * Main navigation container component
 * Flow: 3D Route Overview → Start Navigation → Step-by-step waypoint navigation
 */
const NavigationContainer = ({ onClose }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const {
    route,
    currentStepIndex,
    isComplete,
    nextStep,
    previousStep,
    cancelNavigation,
    error,
    setError,
    setCurrentStepIndex,
  } = useNavigation();

  // Navigation phase: 'overview' | 'stepbystep'
  const [navPhase, setNavPhase] = useState('overview');
  const [currentFloorPlan, setCurrentFloorPlan] = useState(null);
  const [buildingData, setBuildingData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load building data for 3D view
  useEffect(() => {
    if (!route || !route.steps || route.steps.length === 0) {
      setLoading(false);
      return;
    }

    const loadBuildingData = async () => {
      try {
        setLoading(true);

        // Get unique floor plan IDs from route
        const floorPlanIds = [...new Set(route.steps.map(s => s.floor_plan_id || s.floorPlanId).filter(Boolean))];

        if (floorPlanIds.length === 0) {
          setLoading(false);
          return;
        }

        // Load all floor plans with their wayfinding graphs
        const floorsWithData = await Promise.all(
          floorPlanIds.map(async (fpId) => {
            const floorPlan = await indoorMappingService.getFloorPlan(fpId);
            const graph = await indoorMappingService.getWayfindingGraph(fpId);
            return {
              ...floorPlan,
              nodes: graph.nodes || [],
              edges: graph.edges || [],
            };
          })
        );

        setBuildingData({
          floors: floorsWithData.sort((a, b) => (a.floor_number || 0) - (b.floor_number || 0)),
        });

        // Set current floor plan for step-by-step view
        const currentStep = route.steps[currentStepIndex];
        const floorPlanId = currentStep?.floor_plan_id || currentStep?.floorPlanId;
        if (floorPlanId) {
          const fp = floorsWithData.find(f => f.id === floorPlanId);
          setCurrentFloorPlan(fp || floorsWithData[0]);
        }

        setLoading(false);
      } catch (err) {
        console.error('Failed to load building data:', err);
        setError('Failed to load building data');
        setLoading(false);
      }
    };

    loadBuildingData();
  }, [route, setError]);

  // Update current floor plan when step changes
  useEffect(() => {
    if (!route?.steps || !buildingData?.floors) return;

    const currentStep = route.steps[currentStepIndex];
    const floorPlanId = currentStep?.floor_plan_id || currentStep?.floorPlanId;
    if (floorPlanId) {
      const fp = buildingData.floors.find(f => f.id === floorPlanId);
      if (fp) setCurrentFloorPlan(fp);
    }
  }, [currentStepIndex, route, buildingData]);

  // Transform route for 3D viewer
  const navigationRouteFor3D = useMemo(() => {
    if (!route?.steps || !buildingData?.floors) return null;

    // Build node map
    const nodeMap = new Map();
    buildingData.floors.forEach(floor => {
      floor.nodes?.forEach(node => {
        nodeMap.set(node.id, node);
      });
    });

    const routeSteps = [];
    const seenNodes = new Set();

    route.steps.forEach((step) => {
      const startNodeId = step.start_node_id || step.from_node_id;
      const endNodeId = step.end_node_id || step.to_node_id;
      const floorPlanId = step.floor_plan_id || step.floorPlanId;

      if (startNodeId && !seenNodes.has(startNodeId)) {
        const node = nodeMap.get(startNodeId);
        routeSteps.push({
          node_id: startNodeId,
          floor_plan_id: floorPlanId,
          x_position: step.path_geometry?.start?.x ?? node?.x_position ?? 0,
          y_position: step.path_geometry?.start?.y ?? node?.y_position ?? 0,
          node_type: node?.node_type || 'normal',
        });
        seenNodes.add(startNodeId);
      }

      if (endNodeId && !seenNodes.has(endNodeId)) {
        const node = nodeMap.get(endNodeId);
        let nodeType = node?.node_type || 'normal';
        if (step.floor_change_type) {
          nodeType = step.floor_change_type;
        }

        routeSteps.push({
          node_id: endNodeId,
          floor_plan_id: step.floor_change_to || floorPlanId,
          x_position: step.path_geometry?.end?.x ?? node?.x_position ?? 0,
          y_position: step.path_geometry?.end?.y ?? node?.y_position ?? 0,
          node_type: nodeType,
        });
        seenNodes.add(endNodeId);
      }
    });

    return routeSteps.length > 0 ? routeSteps : null;
  }, [route, buildingData]);

  const handleClose = () => {
    cancelNavigation();
    if (onClose) {
      onClose();
    }
  };

  const handleStartNavigation = () => {
    setCurrentStepIndex(0);
    setNavPhase('stepbystep');
  };

  const handleBackToOverview = () => {
    setNavPhase('overview');
  };

  const handleNext = () => {
    if (isComplete) {
      handleClose();
    } else {
      nextStep();
    }
  };

  const currentStep = route?.steps?.[currentStepIndex];
  const totalSteps = route?.steps?.length || 0;

  // Get destination info for arrival screen
  const lastStep = route?.steps?.[totalSteps - 1];
  let rawDestinationPhotoUrl = lastStep?.photo_url || lastStep?.photoUrl;
  // Transform /uploads/ to /api/uploads/ for correct API path
  if (rawDestinationPhotoUrl && rawDestinationPhotoUrl.startsWith('/uploads/')) {
    rawDestinationPhotoUrl = rawDestinationPhotoUrl.replace('/uploads/', '/api/uploads/');
  }
  const destinationPhotoUrl = rawDestinationPhotoUrl;
  const destinationPhotoCaption = lastStep?.photo_caption || lastStep?.photoCaption;
  const openDoorUrl = route?.destination?.metadata?.openDoorUrl
    || route?.destination?.openDoorUrl
    || route?.route?.location?.data?.openDoorUrl;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1300,
          backgroundColor: 'background.default',
        }}
      >
        {/* Loading State */}
        {loading && (
          <Backdrop open sx={{ zIndex: 1400, color: '#fff' }}>
            <CircularProgress color="inherit" />
          </Backdrop>
        )}

        {/* Top Controls */}
        <Box
          sx={{
            position: 'absolute',
            top: 16,
            left: 16,
            right: 16,
            zIndex: 1350,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            pointerEvents: 'none',
          }}
        >
          {/* Left: Close Button */}
          <IconButton
            onClick={handleClose}
            sx={{
              pointerEvents: 'auto',
              backgroundColor: 'background.paper',
              boxShadow: 2,
              '&:hover': { backgroundColor: 'background.paper' },
            }}
          >
            <CloseIcon />
          </IconButton>

          {/* Right: Status Indicator */}
          <Box sx={{ pointerEvents: 'auto' }}>
            {navPhase === 'overview' && (
              <Chip
                icon={<ThreeDIcon />}
                label="Route Preview"
                color="primary"
                sx={{ bgcolor: 'background.paper' }}
              />
            )}
            {navPhase === 'stepbystep' && !isComplete && (
              <Chip
                icon={<NavigationIcon />}
                label={`Step ${currentStepIndex + 1}/${totalSteps}`}
                color="success"
              />
            )}
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ width: '100%', height: '100%' }}>
          {/* 3D Overview Phase */}
          {navPhase === 'overview' && buildingData && (
            <>
              <Building3DViewer
                buildingData={buildingData}
                isMobile={isMobile}
                isNavigating={true}
                navigationRoute={navigationRouteFor3D}
                currentStepIndex={0}
              />

              {/* Start Navigation Button */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1350,
                }}
              >
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<PlayArrowIcon />}
                  onClick={handleStartNavigation}
                  sx={{
                    borderRadius: 3,
                    px: 4,
                    py: 1.5,
                    fontSize: '1.1rem',
                    boxShadow: 4,
                  }}
                >
                  Start Navigation
                </Button>
              </Box>

              {/* Route Info */}
              {route?.route && (
                <Paper
                  sx={{
                    position: 'absolute',
                    bottom: isMobile ? 90 : 24,
                    left: 24,
                    p: 2,
                    zIndex: 1340,
                  }}
                  elevation={3}
                >
                  <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                    Route Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {totalSteps} steps
                    {route.route.total_distance_meters > 0 && ` • ${Math.round(route.route.total_distance_meters)}m`}
                    {route.route.total_time_seconds > 0 && ` • ~${Math.ceil(route.route.total_time_seconds / 60)} min`}
                  </Typography>
                </Paper>
              )}
            </>
          )}

          {/* Step-by-step Navigation Phase */}
          {navPhase === 'stepbystep' && (
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: isMobile ? 'column' : 'row',
                overflow: 'hidden',
              }}
            >
              {/* Map Section */}
              <Box
                sx={{
                  flex: isMobile ? '1 1 50%' : '1 1 60%',
                  minHeight: isMobile ? '40vh' : 'auto',
                  position: 'relative',
                }}
              >
                {currentFloorPlan && route && (
                  <NavigationMap
                    floorPlan={currentFloorPlan}
                    route={route}
                    currentStepIndex={currentStepIndex}
                  />
                )}

                {/* Back to Overview button */}
                <Fab
                  color="primary"
                  size="medium"
                  onClick={handleBackToOverview}
                  sx={{
                    position: 'absolute',
                    bottom: 16,
                    left: 16,
                    zIndex: 10,
                  }}
                >
                  <ArrowBackIcon />
                </Fab>
              </Box>

              {/* Step Card Section */}
              <Box
                sx={{
                  flex: isMobile ? '0 0 auto' : '0 0 40%',
                  maxWidth: isMobile ? '100%' : 450,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: isMobile ? 'flex-start' : 'center',
                  alignItems: 'center',
                  p: 2,
                  backgroundColor: 'background.paper',
                  overflowY: 'auto',
                  boxShadow: isMobile ? '0 -4px 20px rgba(0,0,0,0.15)' : '-4px 0 20px rgba(0,0,0,0.1)',
                }}
              >
                {isComplete ? (
                  <ArrivalScreen
                    destination={route?.destination}
                    destinationPhotoUrl={destinationPhotoUrl}
                    destinationPhotoCaption={destinationPhotoCaption}
                    openDoorUrl={openDoorUrl}
                    onClose={handleClose}
                  />
                ) : (
                  <StepCard
                    step={currentStep}
                    stepNumber={currentStepIndex + 1}
                    totalSteps={totalSteps}
                    onNext={handleNext}
                    onPrevious={previousStep}
                    isLastStep={currentStepIndex === totalSteps - 1}
                  />
                )}
              </Box>
            </Box>
          )}
        </Box>
      </Box>

      {/* Error Snackbar */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {error}
        </Alert>
      </Snackbar>
    </>
  );
};

export default NavigationContainer;
